<template>
	<div class="popper">
		<xyz-transition appear xyz="fade">
			<div class="popover-content" v-if="isOpen">
				<div x-arrow></div>
				<slot></slot>
			</div>
		</xyz-transition>
	</div>
</template>

<script>
import Popper from 'popper.js'

export default {
	name: 'Popover',
	props: {
		name: {
			required: true,
		},
		refElem: {
			default: null,
		},
		placement: {
			default: 'bottom',
		},
		options: {
			default: null,
		},
		delay: {
			default: 0,
		},
		duration: {
			default: 0,
		},
		trigger: {
			default: null,
		},
		show: {
			default: false,
		},
	},
	data() {
		return {
			popper: null,
			timer: null,
		}
	},
	computed: {
		reference() {
			if (this.refElem) {
				return this.refElem
			}
			if (this.$el) {
				return this.$el.parentNode
			}
			return null
		},
		isOpen() {
			return this.$store.state.activePopovers[this.name]
		},
	},
	watch: {
		refElem() {
			this.updateRefElem()
		},
		'$el.parentNode'() {
			this.updateRefElem()
		},
		show() {
			this.refreshShow()
		},
		isOpen() {
			if (this.popper) {
				this.refreshTimer()
				this.popper.scheduleUpdate()
			}
		},
	},
	methods: {
		open() {
			this.$store.dispatch('openPopover', { name: this.name })
		},
		close() {
			this.$store.dispatch('closePopover', { name: this.name })
			this.clearTimer()
		},
		refreshShow() {
			if (this.show) {
				setTimeout(this.open, this.delay)
			} else {
				this.close()
			}
		},
		refreshTimer() {
			this.clearTimer()
			if (this.isOpen && this.duration > 0) {
				this.timer = setTimeout(() => {
					this.close()
				}, this.duration)
			}
		},
		clearTimer() {
			if (this.timer) {
				clearTimeout(this.timer)
				this.timer = null
			}
		},
		onDocumentClick(event) {
			if (!this.popper.reference.contains(event.target)) {
				this.close()
			}
		},
		addEventListeners() {
			if (this.trigger === 'hover') {
				this.popper.reference.addEventListener('mouseenter', this.open)
				this.popper.reference.addEventListener('mouseleave', this.close)
			} else if (this.trigger === 'click') {
				this.popper.reference.addEventListener('click', this.open)
				document.addEventListener('click', this.onDocumentClick)
				document.addEventListener('touchstart', this.onDocumentClick)
			}
		},
		removeEventListeners() {
			this.popper.reference.removeEventListener('mouseenter', this.open)
			this.popper.reference.removeEventListener('mouseleave', this.close)
			this.popper.reference.removeEventListener('click', this.open)
			document.removeEventListener('click', this.onDocumentClick)
			document.removeEventListener('touchstart', this.onDocumentClick)
		},
		updateRefElem() {
			if (this.popper) {
				this.removeEventListeners()
				this.popper.reference = this.reference
				this.addEventListeners()
				this.popper.scheduleUpdate()
			}
		},
		updatePosition() {
			if (this.popper) {
				this.popper.scheduleUpdate()
			}
		},
	},
	created() {
		this.refreshShow()
		this.refreshTimer()
	},
	mounted() {
		const popperOptions = { ...this.options }

		if (this.placement) {
			popperOptions.placement = this.placement
		}

		this.popper = new Popper(this.reference, this.$el, popperOptions)
		this.addEventListeners()
	},
	beforeDestroy() {
		this.close()
		if (this.popper) {
			this.removeEventListeners()
			this.popper.destroy()
		}
	},
}
</script>

<style lang="scss" scoped></style>
